import { useCallback, useEffect, useRef, useState } from 'react';
import { getCeremony } from '../api/ceremonyApi';
import { makeError } from '../api/apiError';

/**
 * 64 lowercase hex characters, matching the Postman collection's description
 * of access_key ("64-char hex access key"). Used only for a fast client-side
 * "is this link well-formed" check — never sent anywhere, never logged.
 */
const ACCESS_KEY_PATTERN = /^[a-f0-9]{64}$/i;

/**
 * Loads a guest's public ceremony data for /ceremony/:ceremonyId?access_key=...
 *
 * - Validates the id/key are present and well-formed BEFORE calling the API
 *   (an "incomplete" link never touches the network or the rate limiter).
 * - Aborts the in-flight request on unmount or when ceremonyId/accessKey
 *   change, and ignores AbortError so it never surfaces as a UI error. This
 *   also makes React 19 StrictMode's dev-mode double-effect harmless.
 * - Never refetches on its own — the effect is keyed only on the two inputs
 *   that define "which ceremony", so no accidental refetch loops.
 */
export function useCeremony(ceremonyId, accessKey) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [retryToken, setRetryToken] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const hasId = typeof ceremonyId === 'string' && ceremonyId.trim().length > 0;
    const hasKey = typeof accessKey === 'string' && ACCESS_KEY_PATTERN.test(accessKey.trim());

    if (!hasId || !hasKey) {
      setState({ status: 'error', data: null, error: makeError('incomplete') });
      return undefined;
    }

    const controller = new AbortController();
    setState({ status: 'loading', data: null, error: null });

    getCeremony(ceremonyId, accessKey, { signal: controller.signal })
      .then((data) => {
        if (!mountedRef.current) return;
        setState({ status: 'success', data, error: null });
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        if (!mountedRef.current) return;
        setState({ status: 'error', data: null, error: error.kind ? error : makeError('server') });
      });

    return () => controller.abort();
  }, [ceremonyId, accessKey, retryToken]);

  const retry = useCallback(() => setRetryToken((n) => n + 1), []);

  return { ...state, retry };
}
